import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import api from "../../api"; // axios instance with token + school_id interceptor

const ProductCategory = () => {
  const [form, setForm] = useState({ category_name: "" });
  const [categories, setCategories] = useState([]);
  const [msg, setMsg] = useState(null);
  const [editId, setEditId] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const baseURL = "/product/category";
  const schoolId = localStorage.getItem("school_id");

  // ================= FETCH CATEGORIES =================
const fetchCategories = async () => {
  try {
    const res = await api.get(`${baseURL}/list`, {
      headers: { "X-School-Id": schoolId },
    });

    console.log("Raw response:", res.data);
    const data = res.data;

    // Your backend is returning an array directly
    // like: [{"id":1,"category_name":"Books"}, ...]
    const list = Array.isArray(data) ? data : data?.data ?? [];

    setCategories(list);
  } catch (err) {
    console.error("Error fetching categories:", err);
    setCategories([]);
  }
};


  useEffect(() => {
    fetchCategories();
  }, []);

  // ================= HANDLE INPUT =================
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ================= ADD / UPDATE CATEGORY =================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);

    if (!form.category_name.trim()) {
      return setMsg({ type: "error", text: "Category name is required." });
    }

    try {
      let res;
      if (editId) {
        res = await api.post(`${baseURL}/update/${editId}`, form, {
          headers: { "X-School-Id": schoolId },
        });
      } else {
        res = await api.post(`${baseURL}/add`, form, {
          headers: { "X-School-Id": schoolId },
        });
      }

      const json = res.data;
      if (json?.status || json?.success) {
        setMsg({
          type: "success",
          text: json.message || (editId ? "Updated successfully" : "Added successfully"),
        });
        setForm({ category_name: "" });
        setEditId(null);
        fetchCategories();
      } else {
        setMsg({ type: "error", text: json?.message || "Operation failed." });
      }
    } catch (err) {
      console.error("Error saving category:", err);
      setMsg({ type: "error", text: "Network or server error." });
    }
  };

  // ================= DELETE CATEGORY =================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      const res = await api.post(`${baseURL}/delete/${id}`, {}, {
        headers: { "X-School-Id": schoolId },
      });

      const json = res.data;
      if (json?.status || json?.success) {
        setMsg({ type: "success", text: json.message || "Deleted successfully." });
        fetchCategories();
      } else {
        setMsg({ type: "error", text: json?.message || "Delete failed." });
      }
    } catch (err) {
      console.error("Delete error:", err);
      setMsg({ type: "error", text: "Network or server error." });
    }
  };

  // ================= EDIT CATEGORY =================
  const handleEdit = (item) => {
    setForm({ category_name: item.category_name });
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main
          style={{
            padding: "30px",
            maxWidth: "1000px",
            margin: "auto",
            marginTop: "0px",
          }}
        >
          {/* Add/Edit Category Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>
              {editId ? "✏️ Edit Category" : "➕ Add New Category"}
            </h2>
            {msg && <p style={{ color: msg.type === "error" ? "#b91c1c" : "#166534" }}>{msg.text}</p>}
            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                <FormGroup label="Category Name">
                  <input
                    type="text"
                    name="category_name"
                    value={form.category_name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>
              </div>
              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle}>
                  {editId ? "Update Category" : "Add Category"}
                </button>
              </div>
            </form>
          </section>

          {/* View Categories Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📋 All Categories</h2>
            {categories.length === 0 ? (
              <p>No categories found.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {categories.map((item, idx) => (
                  <li
                    key={item.id}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      borderRadius: "8px",
                      background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "16px", color: "#333" }}>
                        {item.category_name}
                      </strong>
                      <div style={{ color: "#777", fontSize: "13px" }}>
                        Status: {item.status} | Created: {item.created_at}
                      </div>
                    </div>
                    <div>
                      <FaEdit
                        onClick={() => handleEdit(item)}
                        style={{
                          color: "#0077cc",
                          cursor: "pointer",
                          marginRight: "15px",
                        }}
                      />
                      <FaTrash
                        onClick={() => handleDelete(item.id)}
                        style={{ color: "red", cursor: "pointer" }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

// Reusable Form Group
const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "20px",
};

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
  boxSizing: "border-box",
};

const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style }}>
    <label
      style={{
        marginBottom: "6px",
        display: "block",
        fontWeight: 500,
        color: "#444",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "30px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "25px",
};

export default ProductCategory;
